import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { AuthenticationDataService } from '@omni/data-services/authentication/authentication.service';
import { InterceptorSkipHeader } from '@omni/interceptors/token.interceptor';
import { DiskService } from '@omni/services/disk/disk.service';

/** Microsoft Graph resource for the signed-in user's profile photo. */
export const GRAPH_ME_PHOTO_URL = 'https://graph.microsoft.com/v1.0/me/photo';

/** The only Graph permission the photo needs; a narrower token than the app's full Graph grant. */
export const GRAPH_USER_READ_SCOPE = 'https://graph.microsoft.com/User.Read';

/** The signed-in user's photo as cached in the user's offline database. */
interface CachedUserPhoto {
  etag: string;
  dataUrl: string;
}

/**
 * Provides the signed-in user's Microsoft 365 profile photo from Microsoft Graph, cached in the user's offline
 * database so that it also shows offline. The photo is downloaded again only when its ETag changes. It is null
 * when the user has no photo. The Graph token is requested for `User.Read` only; when no token is returned, or a
 * request fails for another reason such as no network, the cached photo stays.
 */
@Injectable({ providedIn: 'root' })
export class UserProfilePhotoService {
  /** Data URL of the signed-in user's photo, or null when there is none. */
  readonly photo$ = new BehaviorSubject<string | null>(null);

  /** Refresh in progress, shared by concurrent callers. */
  #refresh: Promise<void> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly authData: AuthenticationDataService,
    private readonly disk: DiskService,
  ) {}

  /**
   * Shows the cached photo at once and, when online, brings it up to date from Graph.
   *
   * @param online Whether the device is online; offline, only the cached photo is used.
   * @returns A promise that settles once the photo is up to date.
   */
  async load(online: boolean): Promise<void> {
    const cached = await this.#readCache();
    this.photo$.next(cached?.dataUrl ?? null);
    if (!online) {
      return;
    }
    this.#refresh ??= this.#refreshFromGraph(cached).finally(
      () => (this.#refresh = null),
    );
    await this.#refresh;
  }

  /**
   * Compares the Graph photo's ETag with the cached one and downloads the photo when it differs.
   *
   * @param cached Photo cached for the signed-in user, if any.
   */
  async #refreshFromGraph(cached: CachedUserPhoto | null): Promise<void> {
    try {
      const accessToken = await this.authData.getToken(GRAPH_USER_READ_SCOPE);
      if (!accessToken?.token) {
        return;
      }
      const headers = new HttpHeaders()
        .set('Authorization', `Bearer ${accessToken.token}`)
        .set(InterceptorSkipHeader, 'true');
      const metadata = await firstValueFrom(
        this.http.get<Record<string, string | undefined>>(GRAPH_ME_PHOTO_URL, {
          headers,
        }),
      );
      const etag = metadata['@odata.mediaEtag'] ?? '';
      if (cached && etag && cached.etag === etag) {
        return;
      }
      const image = await firstValueFrom(
        this.http.get(`${GRAPH_ME_PHOTO_URL}/$value`, {
          headers,
          responseType: 'blob',
        }),
      );
      const photo: CachedUserPhoto = { etag, dataUrl: await toDataUrl(image) };
      await this.disk.updateOrInsert(
        DB_KEY_PREFIXES.USER_PROFILE_PHOTO,
        () => photo,
      );
      this.photo$.next(photo.dataUrl);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        await this.disk.remove(DB_KEY_PREFIXES.USER_PROFILE_PHOTO);
        this.photo$.next(null);
      }
    }
  }

  /**
   * Reads the cached photo from the user's offline database.
   *
   * @returns The cached photo, or null when none is cached or the database cannot be read.
   */
  async #readCache(): Promise<CachedUserPhoto | null> {
    try {
      const doc = await this.disk.retrieve(
        DB_KEY_PREFIXES.USER_PROFILE_PHOTO,
        true,
      );
      return doc?.dataUrl
        ? { etag: doc.etag ?? '', dataUrl: doc.dataUrl }
        : null;
    } catch {
      return null;
    }
  }
}

/**
 * Encodes an image as a data URL.
 *
 * @param image Image returned by Graph.
 * @returns The data URL, typed with the image's content type.
 */
async function toDataUrl(image: Blob): Promise<string> {
  const bytes = new Uint8Array(await image.arrayBuffer());
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return `data:${image.type || 'image/jpeg'};base64,${btoa(binary)}`;
}
